<template>
  <ListingsFiltersSection
    title="Contact Type"
    :error-message="!filters.contact_type.length ? 'Please select at least one contact type' : undefined"
  >
    <VCheckbox
      v-model="filters.contact_type"
      :value="EContactType.VENDOR"
      label="Vendors"
      class="listings-filters"
    />
    <VCheckbox
      v-model="filters.contact_type"
      :value="EContactType.LANDLORD"
      label="Landlords"
      class="listings-filters"
    />
    <VCheckbox
      v-model="filters.contact_type"
      :value="EContactType.APPLICANT"
      label="Applicants"
      class="listings-filters"
    />
  </ListingsFiltersSection>
  <ListingsFiltersSection title="Show contacts since" class="show-contacts-since">
    <ListingsFilterControl label="Vendors since">
      <DatePicker
        v-model="filters.vendor_since"
        placeholder="Select date"
        :disabled="!filters.contact_type.includes(EContactType.VENDOR)"
      />
    </ListingsFilterControl>
    <ListingsFilterControl label="Landlords since">
      <DatePicker
        v-model="filters.landlord_since"
        placeholder="Select date"
        :disabled="!filters.contact_type.includes(EContactType.LANDLORD)"
      />
    </ListingsFilterControl>
    <ListingsFilterControl label="Applicants since">
      <DatePicker
        v-model="filters.applicant_since"
        placeholder="Select date"
        :disabled="!filters.contact_type.includes(EContactType.APPLICANT)"
      />
    </ListingsFilterControl>
    <ListingsPropertyStatusDropdown
      v-model="filters.entity_status"
      :data-client="opportunityDataClient"
    />
    <ListingsFilterControl label="Estate agency">
      <VDropdown v-model="filters.show_stock_for" :options="STOCK_OPTIONS" />
    </ListingsFilterControl>
  </ListingsFiltersSection>
  <ListingsFiltersSection title="Activity">
    <ListingsCheckboxFilter
      v-model="unactionedOnly"
      label="Unactioned listings only"
      explanation="Generate emails only for listings that have not been actioned yet."
    />
  </ListingsFiltersSection>
</template>

<script lang="ts" setup>
  // Types
  import type { TListingFilters, TOpportunityDataClientModel } from '~/misc/listings/types';
  import type { AbstractDataClient } from '~/misc/services/data-client';
  import { EContactType } from '~/misc/listings/types';

  // Config
  import { STOCK_OPTIONS } from '~/misc/listings/list/dropdown/config';

  // Components
  import ListingsFiltersSection from '~/misc/listings/filters/section/ListingsFiltersSection.vue';
  import ListingsFilterControl from '~/misc/listings/filters/control/ListingsFilterControl.vue';
  import ListingsPropertyStatusDropdown from '~/misc/listings/filters/data/property-status/ListingsPropertyStatusDropdown.vue';
  import ListingsCheckboxFilter from '~/misc/listings/filters/checkbox/ListingsCheckboxFilter.vue';
  import DatePicker from '~/misc/date-picker/DatePicker.vue';
  import VCheckbox from '~/misc/v-checkbox';
  import VDropdown from '~/misc/v-dropdown';

  defineProps({
    opportunityDataClient: {
      type: Object as PropType<AbstractDataClient<TOpportunityDataClientModel> | undefined>,
      required: false,
      default: undefined,
    },
  });

  const filters = defineModel({
    type: Object as PropType<TListingFilters>,
    required: true,
  });

  const unactionedOnly: WritableComputedRef<boolean> = computed<boolean>({
    get(): boolean {
      return !!filters.value.exclude_actioned_properties;
    },
    set(value: boolean): void {
      filters.value.exclude_actioned_properties = Number(value);
    },
  });

  watch(
    () => filters.value.contact_type,
    (newVal: EContactType[]) => {
      // Clear the "since" dates for deselected contact types
      if (!newVal.includes(EContactType.VENDOR)) {
        filters.value.vendor_since = null;
      }

      if (!newVal.includes(EContactType.LANDLORD)) {
        filters.value.landlord_since = null;
      }

      if (!newVal.includes(EContactType.APPLICANT)) {
        filters.value.applicant_since = null;
      }
    }
  );
</script>
